import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Plus, Search, Star, Loader2, ChevronRight, Trophy, Target, Upload, Users, Trash2, GitCompare, X,
} from "lucide-react";
import { toast } from "sonner";
import { fmtNum, fmtPts } from "@/lib/datasetFmt";
import RivalDatasetCompareDialog from "@/components/RivalDatasetCompareDialog";

const blankForm = () => ({
  jugador: "", anio_nacimiento: "", posicion: "", rival_actual: "",
  codigo_rffm: "", ficha_rffm: "", notas: "", equipo_ef: "",
});

export default function RivalsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState(() => new URLSearchParams(window.location.search).get("view") || "players");
  const [meta, setMeta] = useState(null);
  const [equipoEf, setEquipoEf] = useState(() => new URLSearchParams(window.location.search).get("ef") || "");
  const [q, setQ] = useState(() => new URLSearchParams(window.location.search).get("rival") || "");
  const [posFilter, setPosFilter] = useState(() => new URLSearchParams(window.location.search).get("pos") || "any");
  const [yearFilter, setYearFilter] = useState(() => new URLSearchParams(window.location.search).get("anio") || "any");
  const [origenFilter, setOrigenFilter] = useState(() => new URLSearchParams(window.location.search).get("origen") || "any");
  const [decisionFilter, setDecisionFilter] = useState(() => new URLSearchParams(window.location.search).get("decision") || "any");

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const setOrDel = (k, v, def) => { if (v && v !== def) p.set(k, v); else p.delete(k); };
    setOrDel("view", view, "players");
    setOrDel("ef", equipoEf !== "all" ? equipoEf : "", "");
    setOrDel("rival", q.trim(), "");
    setOrDel("pos", posFilter, "any");
    setOrDel("anio", yearFilter, "any");
    setOrDel("origen", origenFilter, "any");
    setOrDel("decision", decisionFilter, "any");
    const qs = p.toString();
    window.history.replaceState(null, "", window.location.pathname + (qs ? `?${qs}` : ""));
  }, [view, equipoEf, q, posFilter, yearFilter, origenFilter, decisionFilter]);
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ jugadores: 0, veces_destacados: 0, equipos_rivales: 0 });
  const [directory, setDirectory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blankForm());
  const [compareSel, setCompareSel] = useState([]);
  const [compareOpen, setCompareOpen] = useState(false);

  const toggleCompare = (rp) => {
    setCompareSel((sel) => {
      if (sel.some((x) => x.jugador_id === rp.jugador_id)) {
        return sel.filter((x) => x.jugador_id !== rp.jugador_id);
      }
      if (sel.length >= 3) {
        toast.info("Máximo 3 jugadores para comparar");
        return sel;
      }
      return [...sel, rp];
    });
  };

  const isAdmin = user?.role === "admin";

  useEffect(() => {
    api.get("/rival-dataset/meta").then((r) => {
      setMeta(r.data);
      const saved = localStorage.getItem("rivals_equipo_ef");
      setEquipoEf((curr) => {
        if (curr) return curr;
        if (saved && (saved === "all" || r.data.equipos_ef.includes(saved))) return saved;
        if (r.data.mis_equipos?.length) return r.data.mis_equipos[0];
        return "all";
      });
    }).catch((err) => toast.error(formatApiError(err)));
  }, []);

  const pickEquipo = (v) => {
    setEquipoEf(v);
    localStorage.setItem("rivals_equipo_ef", v);
  };

  const loadPlayers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (equipoEf && equipoEf !== "all") params.append("equipo_ef", equipoEf);
      if (q.trim()) params.append("q", q.trim());
      if (posFilter !== "any") params.append("posicion", posFilter);
      if (yearFilter !== "any") params.append("anio", yearFilter);
      if (origenFilter !== "any") params.append("origen", origenFilter);
      if (decisionFilter !== "any") params.append("decision", decisionFilter);
      const r = await api.get(`/rival-dataset/players?${params.toString()}`);
      setItems(r.data.items || []);
      setSummary(r.data.summary || {});
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const loadDirectory = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (equipoEf && equipoEf !== "all") params.append("equipo_ef", equipoEf);
      const r = await api.get(`/rival-dataset/directory?${params.toString()}`);
      setDirectory(r.data || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!equipoEf) return;
    const t = setTimeout(() => {
      if (view === "players") loadPlayers();
      else loadDirectory();
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equipoEf, q, posFilter, yearFilter, origenFilter, decisionFilter, view]);

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      jugador: form.jugador.trim(),
      anio_nacimiento: form.anio_nacimiento ? parseInt(form.anio_nacimiento, 10) : null,
      posicion: form.posicion.trim(),
      rival_actual: form.rival_actual.trim(),
      codigo_rffm: form.codigo_rffm.trim(),
      ficha_rffm: form.ficha_rffm.trim(),
      notas: form.notas.trim(),
      equipo_ef: form.equipo_ef || (equipoEf !== "all" ? equipoEf : ""),
    };
    if (!payload.jugador) { toast.error("Nombre obligatorio"); return; }
    try {
      const r = await api.post("/rival-dataset/players", payload);
      if (r.data.duplicate) {
        toast.info(`Este jugador ya existe: ${r.data.player.jugador}`);
        navigate(`/rivals/jugador/${r.data.player.jugador_id}`);
      } else {
        toast.success("Jugador rival creado");
        loadPlayers();
      }
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removeManual = async (rp, e) => {
    e.stopPropagation();
    if (!window.confirm(`¿Eliminar "${rp.jugador}"?`)) return;
    try {
      await api.delete(`/rival-dataset/players/${rp.jugador_id}`);
      toast.success("Eliminado");
      loadPlayers();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const importExcel = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/rival-dataset/import", fd);
      toast.success(`Excel importado: ${r.data.creados} nuevos, ${r.data.actualizados} actualizados`);
      const m = await api.get("/rival-dataset/meta");
      setMeta(m.data);
      loadPlayers();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setImporting(false);
    }
  };

  const toggleBar = (
    <div className="flex gap-1 border-b border-slate-200">
      {[["players", "Jugadores rivales"], ["directory", "Directorio de equipos"]].map(([k, l]) => (
        <button
          key={k}
          onClick={() => setView(k)}
          className={`px-3 py-2 font-bold text-sm -mb-px border-b-2 transition ${
            view === k ? "border-purple-700 text-purple-800" : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
          data-testid={`rivals-view-${k}`}
        >
          {l}
        </button>
      ))}
    </div>
  );

  const equipoSelect = meta && (
    <div>
      <Label className="text-xs">Equipo EF Concepción</Label>
      <Select value={equipoEf} onValueChange={pickEquipo}>
        <SelectTrigger data-testid="rivals-team-filter">
          <SelectValue placeholder="Equipo" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Todos los equipos</SelectItem>
          {meta.equipos_ef.map((t) => (
            <SelectItem key={t} value={t}>{t}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black" style={{ fontFamily: "Nunito" }}>
            Mis rivales
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Base de datos de jugadores rivales conectada al estudio RFFM.
          </p>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <>
              <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={importExcel} />
              <Button
                variant="outline"
                onClick={() => fileRef.current?.click()}
                disabled={importing}
                className="border-purple-300 text-purple-700 hover:bg-purple-50 font-bold"
                data-testid="rivals-import-excel"
              >
                {importing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                Actualizar Excel
              </Button>
            </>
          )}
          <Button
            onClick={() => { setForm(blankForm()); setOpen(true); }}
            className="bg-purple-600 hover:bg-purple-700 text-white font-bold"
            data-testid="rivals-new-button"
          >
            <Plus className="h-4 w-4 mr-2" /> Nuevo rival
          </Button>
        </div>
      </div>

      {toggleBar}

      {view === "players" && (
        <>
          {/* Stats strip */}
          <div className="grid grid-cols-3 gap-3">
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Jugadores</div>
                <div className="text-2xl font-black text-purple-700" data-testid="rivals-count">
                  {summary.jugadores || 0}
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Veces destacados</div>
                <div className="text-2xl font-black text-purple-700" data-testid="rivals-highlighted-count">
                  {summary.veces_destacados || 0}
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Equipos rivales</div>
                <div className="text-2xl font-black text-purple-700" data-testid="rivals-teams-count">
                  {summary.equipos_rivales || 0}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <Card className="border-slate-200">
            <CardContent className="p-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {equipoSelect}
              <div>
                <Label className="text-xs">Buscar</Label>
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Nombre, club rival o código RFFM"
                    className="pl-9"
                    data-testid="rivals-search"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Posición</Label>
                <Select value={posFilter} onValueChange={setPosFilter}>
                  <SelectTrigger data-testid="rivals-position-filter">
                    <SelectValue placeholder="Cualquiera" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Cualquier posición</SelectItem>
                    {(meta?.posiciones || []).map((p) => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Año de nacimiento</Label>
                <Select value={yearFilter} onValueChange={setYearFilter}>
                  <SelectTrigger data-testid="rivals-year-filter">
                    <SelectValue placeholder="Cualquiera" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Cualquier año</SelectItem>
                    {(meta?.anios || []).map((a) => (
                      <SelectItem key={a} value={String(a)}>{a}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Origen scouting</Label>
                <Select value={origenFilter} onValueChange={setOrigenFilter}>
                  <SelectTrigger data-testid="rivals-origin-filter">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Todos los orígenes</SelectItem>
                    <SelectItem value="destacado">Destacado en partido</SelectItem>
                    <SelectItem value="informe">Con informe manual</SelectItem>
                    <SelectItem value="nueva_alta">Nueva alta RFFM</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Decisión</Label>
                <Select value={decisionFilter} onValueChange={setDecisionFilter}>
                  <SelectTrigger data-testid="rivals-decision-filter">
                    <SelectValue placeholder="Cualquiera" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Cualquier decisión</SelectItem>
                    {["Fichar", "Seguir", "Ver en directo", "Descartar", "Pendiente"].map((d) => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Player list */}
          <Card className="border-slate-200">
            <CardContent className="p-0">
              {loading ? (
                <div className="p-6 text-sm text-slate-500 flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" /> Cargando...
                </div>
              ) : items.length === 0 ? (
                <div className="p-10 text-center text-slate-400 text-sm">
                  No hay jugadores rivales con esos filtros.
                </div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {items.map((rp) => (
                    <li
                      key={rp.jugador_id}
                      onClick={() => navigate(`/rivals/jugador/${rp.jugador_id}`)}
                      className="flex items-center gap-3 p-4 hover:bg-purple-50/40 cursor-pointer"
                      data-testid={`rival-card-${rp.jugador_id}`}
                    >
                      <div className="w-10 h-10 rounded-lg bg-purple-100 text-purple-800 grid place-items-center shrink-0 font-black text-sm">
                        {rp.ranking_general != null ? `#${rp.ranking_general}` : "—"}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-bold text-slate-900 truncate">{rp.jugador}</div>
                        <div className="text-xs text-slate-500 flex flex-wrap gap-x-2">
                          <span>{rp.posicion || "Sin posición"}</span>
                          {rp.anio_nacimiento && <span>· {rp.anio_nacimiento}</span>}
                          {rp.rival_actual && <span>· {rp.rival_actual}</span>}
                        </div>
                      </div>
                      {rp.puntuacion != null && (
                        <div className="text-right hidden sm:block">
                          <div className="text-sm font-black" style={{ color: "#D9B300" }}>{fmtPts(rp.puntuacion)}</div>
                          <div className="text-[9px] uppercase tracking-widest text-slate-400">Puntos</div>
                        </div>
                      )}
                      {rp.veces_destacado > 0 && (
                        <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-purple-100 text-purple-800 text-xs font-bold shrink-0">
                          <Star className="h-3 w-3" /> {rp.veces_destacado}×
                        </span>
                      )}
                      <Button
                        variant={compareSel.some((x) => x.jugador_id === rp.jugador_id) ? "default" : "outline"}
                        size="sm"
                        className={`shrink-0 h-7 px-2 text-[11px] font-bold ${compareSel.some((x) => x.jugador_id === rp.jugador_id) ? "bg-purple-600 hover:bg-purple-700 text-white" : "border-purple-300 text-purple-700"}`}
                        onClick={(e) => { e.stopPropagation(); toggleCompare(rp); }}
                        data-testid={`rival-compare-toggle-${rp.jugador_id}`}
                      >
                        <GitCompare className="h-3 w-3 mr-1" /> Comparar
                      </Button>
                      {rp.source === "manual" && isAdmin && (
                        <Button variant="ghost" size="icon" onClick={(e) => removeManual(rp, e)} data-testid={`rival-delete-${rp.jugador_id}`}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      )}
                      <ChevronRight className="h-4 w-4 text-slate-300 shrink-0" />
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {view === "directory" && (
        <>
          <Card className="border-slate-200">
            <CardContent className="p-4 grid grid-cols-1 md:grid-cols-3 gap-3">{equipoSelect}</CardContent>
          </Card>
          {loading ? (
            <div className="p-6 text-sm text-slate-500 flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Cargando...
            </div>
          ) : directory.length === 0 ? (
            <div className="p-10 text-center text-slate-400 text-sm">No hay equipos rivales para este filtro.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {directory.map((d) => (
                <Card
                  key={`${d.rival}|${d.equipo_ef || ""}`}
                  onClick={() => navigate(`/rivals/equipo/${encodeURIComponent(d.rival)}${d.equipo_ef ? `?ef=${encodeURIComponent(d.equipo_ef)}` : ""}`)}
                  className="border-slate-200 hover:border-purple-400 hover:shadow-md transition cursor-pointer"
                  data-testid={`directory-card-${d.rival}`}
                >
                  <CardContent className="p-5 space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-black text-slate-900 leading-tight" style={{ fontFamily: "Nunito" }}>
                        {d.rival}
                      </div>
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold text-white shrink-0" style={{ background: "#5B2383" }}>
                        {d.jugadores} jug.
                      </span>
                    </div>
                    <div className="text-xs text-slate-500 flex items-center gap-1.5">
                      <Users className="h-3.5 w-3.5" /> Rival de {d.equipos_ef.join(" / ") || "—"}
                    </div>
                    <div className="space-y-1.5 text-xs">
                      <div className="flex items-center gap-1.5 text-slate-700">
                        <Trophy className="h-3.5 w-3.5" style={{ color: "#D9B300" }} />
                        <span className="font-semibold truncate">{d.mejor_jugador}</span>
                        <span className="text-slate-400 shrink-0">{fmtPts(d.mejor_puntuacion)} pts</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-slate-700">
                        <Target className="h-3.5 w-3.5 text-emerald-600" />
                        <span className="font-semibold truncate">{d.goleador}</span>
                        <span className="text-slate-400 shrink-0">{d.goleador_goles} goles</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between pt-1 border-t border-slate-100 text-[11px] text-slate-500">
                      <span>{fmtNum(d.goles_total)} goles · {d.temporadas} temp.</span>
                      <span className="font-bold text-purple-700 inline-flex items-center">
                        Ver rival <ChevronRight className="h-3.5 w-3.5" />
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      {/* New manual player dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuevo jugador rival</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div>
              <Label>Nombre *</Label>
              <Input value={form.jugador} onChange={(e) => setForm({ ...form, jugador: e.target.value })} required data-testid="rival-page-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Posición</Label>
                <Input value={form.posicion} onChange={(e) => setForm({ ...form, posicion: e.target.value })} placeholder="Ej. Delantero" data-testid="rival-page-position" />
              </div>
              <div>
                <Label>Año de nacimiento</Label>
                <Input type="number" min="1900" max="2099" value={form.anio_nacimiento} onChange={(e) => setForm({ ...form, anio_nacimiento: e.target.value })} placeholder="Ej. 2010" data-testid="rival-page-year" />
              </div>
            </div>
            <div>
              <Label>Club rival</Label>
              <Input value={form.rival_actual} onChange={(e) => setForm({ ...form, rival_actual: e.target.value })} placeholder="Nombre del club rival" data-testid="rival-page-team" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Código RFFM</Label>
                <Input value={form.codigo_rffm} onChange={(e) => setForm({ ...form, codigo_rffm: e.target.value })} data-testid="rival-page-code" />
              </div>
              <div>
                <Label>Equipo EF</Label>
                <Select value={form.equipo_ef || "none"} onValueChange={(v) => setForm({ ...form, equipo_ef: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="rival-page-equipo-ef"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    {(meta?.equipos_ef || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>URL ficha RFFM (opcional)</Label>
              <Input type="url" value={form.ficha_rffm} onChange={(e) => setForm({ ...form, ficha_rffm: e.target.value })} placeholder="https://www.rffm.es/..." data-testid="rival-page-rffm-url" />
            </div>
            <div>
              <Label>Notas de scouting</Label>
              <Textarea rows={3} value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} data-testid="rival-page-notes" />
            </div>
            <DialogFooter>
              <Button type="submit" className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="rival-page-save">
                Crear
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Barra flotante de comparación */}
      {compareSel.length > 0 && (
        <div
          className="fixed bottom-20 md:bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-2 bg-white border border-purple-200 shadow-lg rounded-full px-4 py-2"
          data-testid="rival-compare-bar"
        >
          <span className="text-xs font-bold text-slate-600">
            {compareSel.length} seleccionado{compareSel.length > 1 ? "s" : ""}
          </span>
          <Button
            size="sm"
            className="bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-full"
            disabled={compareSel.length < 2}
            onClick={() => setCompareOpen(true)}
            data-testid="rival-compare-open-btn"
          >
            <GitCompare className="h-4 w-4 mr-1.5" /> Comparar ({compareSel.length}/3)
          </Button>
          <Button variant="ghost" size="sm" className="rounded-full" onClick={() => setCompareSel([])} data-testid="rival-compare-clear-btn">
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      <RivalDatasetCompareDialog open={compareOpen} onOpenChange={setCompareOpen} players={compareSel} />
    </div>
  );
}
